import { forwardRef, type HTMLAttributes } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar.js";
import { Chip } from "./chip.js";
import { LeagueBadge, type League } from "./league-badge.js";
import { RatingBadge } from "./rating-badge.js";
import { cn } from "./lib/cn.js";

/**
 * The fundamental display unit for a Agent. Composes Avatar + handle +
 * LeagueBadge + RatingBadge with optional Archetype and Provisional chips.
 *
 * Two layouts:
 * - `layout="row"` (default) — horizontal, dense, used in leaderboards
 * and sidebars.
 * - `layout="card"` — vertical block, used in profile previews.
 */

export type Archetype = "sniper" | "brawler" | "scout" | "generalist";

export interface Agent {
 handle: string;
 league: League;
 rating: number;
 ratingCI?: number;
 ratingDelta?: number | null;
 provisional?: boolean;
 archetype?: Archetype;
 /** Optional portrait URL. Falls back to the handle's initials. */
 portraitUrl?: string;
}

export interface AgentCardProps extends HTMLAttributes<HTMLDivElement> {
 agent: Agent;
 layout?: "row" | "card";
}

function initialsFromHandle(handle: string): string {
 const parts = handle.split(/[._-]/).filter((p) => p.length > 0);
 if (parts.length >= 2 && parts[0].length > 0 && parts[1].length > 0) {
 return (parts[0][0]! + parts[1][0]!).toLowerCase();
 }
 const flat = handle.replace(/[._-]/g, "");
 if (flat.length >= 2) return flat.slice(0, 2).toLowerCase();
 if (flat.length === 1) return flat.toLowerCase();
 return "··";
}

export const AgentCard = forwardRef<HTMLDivElement, AgentCardProps>(
 ({ agent, layout = "row", className, ...props }, ref) => {
 const initials = initialsFromHandle(agent.handle);
 const wrapperCls =
 layout === "row"
 ? "flex items-center gap-3"
 : "flex flex-col items-start gap-2 p-3 border border-[var(--pp-border)] bg-[var(--pp-bg-strong)] ";

 return (
 <div ref={ref} className={cn(wrapperCls, className)} {...props}>
 <Avatar size={layout === "card" ? "lg" : "md"}>
 {agent.portraitUrl && <AvatarImage src={agent.portraitUrl} alt={agent.handle} />}
 <AvatarFallback initials={initials} />
 </Avatar>

 <div
 style={{
 display: "flex",
 flexDirection: "column",
 gap: 4,
 minWidth: 0,
 }}
 >
 <div
 style={{
 display: "flex",
 alignItems: "baseline",
 gap: 8,
 flexWrap: "wrap",
 }}
 >
 <span
 style={{
 fontFamily: "var(--pp-font-family)",
 fontSize: "var(--pp-font-md)",
 color: "var(--pp-text)",
 fontWeight: 500,
 }}
 >
 {agent.handle}
 </span>
 <LeagueBadge league={agent.league} />
 </div>
 <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
 <RatingBadge
 rating={agent.rating}
 ci={agent.ratingCI}
 delta={agent.ratingDelta}
 provisional={agent.provisional}
 />
 {agent.archetype && (
 <Chip tone="muted" aria-label={`Archetype: ${agent.archetype}`}>
 {agent.archetype.toUpperCase()}
 </Chip>
 )}
 </div>
 </div>
 </div>
 );
 },
);

AgentCard.displayName = "AgentCard";
